import { Link } from "react-router-dom";
import {
  FiHome,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
  FiDollarSign,
  FiFileText,
  FiPercent,
  FiBarChart2,
  FiArrowRight,
  FiLayers,
} from "@/lib/icons";
import type { ReactNode } from "react";

interface MenuLink {
  label: string;
  href: string;
  description: string;
  icon: ReactNode;
}

const homeLoansLinks: MenuLink[] = [
  {
    label: "Buying a Home",
    href: "/home-loans/buying-a-home",
    description: "Guidance on purchasing your dream home",
    icon: <FiHome className="size-5" />,
  },
  {
    label: "First Home Buyer",
    href: "/home-loans/first-home-buyer",
    description: "Grants, schemes & expert guidance",
    icon: <FiHome className="size-5" />,
  },
  {
    label: "Refinancing",
    href: "/home-loans/refinancing",
    description: "Lower your rate or access equity",
    icon: <FiRefreshCw className="size-5" />,
  },
  {
    label: "Investment Property",
    href: "/home-loans/investment",
    description: "Build your property portfolio",
    icon: <FiTrendingUp className="size-5" />,
  },
  {
    label: "Construction Loans",
    href: "/home-loans/construction",
    description: "Build your dream home",
    icon: <FiHome className="size-5" />,
  },
  {
    label: "Bridging Loans",
    href: "/home-loans/bridging",
    description: "Buy before you sell",
    icon: <FiLayers className="size-5" />,
  },
];

const businessLinks: MenuLink[] = [
  {
    label: "Asset Finance",
    href: "/business-loans/asset-finance",
    description: "Finance equipment & vehicles",
    icon: <FiDollarSign className="size-5" />,
  },
  {
    label: "Invoice Finance",
    href: "/business-loans/invoice-finance",
    description: "Unlock cash from invoices",
    icon: <FiFileText className="size-5" />,
  },
  {
    label: "Term Loans",
    href: "/business-loans/term-loans",
    description: "Flexible business funding",
    icon: <FiDollarSign className="size-5" />,
  },
];

const calculatorLinks: MenuLink[] = [
  {
    label: "Borrowing Power",
    href: "/calculators/borrowing-power",
    description: "How much can you borrow?",
    icon: <FiTrendingUp className="size-5" />,
  },
  {
    label: "Repayments",
    href: "/calculators/repayments",
    description: "Calculate your repayments",
    icon: <FiPercent className="size-5" />,
  },
  {
    label: "Stamp Duty",
    href: "/calculators/stamp-duty",
    description: "Estimate costs by state",
    icon: <FiDollarSign className="size-5" />,
  },
  {
    label: "Extra Repayments",
    href: "/calculators/extra-repayments",
    description: "Save on interest",
    icon: <FiBarChart2 className="size-5" />,
  },
];

const guideLinks: MenuLink[] = [
  {
    label: "The Loan Process",
    href: "/guides/loan-process",
    description: "Step-by-step guide",
    icon: <FiFileText className="size-5" />,
  },
  {
    label: "First Home Buyer Guide",
    href: "/guides/first-home-buyer-guide",
    description: "Grants & schemes by state",
    icon: <FiHome className="size-5" />,
  },
  {
    label: "Refinancing Guide",
    href: "/guides/refinancing-guide",
    description: "When & how to refinance",
    icon: <FiRefreshCw className="size-5" />,
  },
  {
    label: "Why Use a Broker",
    href: "/guides/why-use-a-broker",
    description: "Benefits of using a broker",
    icon: <FiUsers className="size-5" />,
  },
];

function MenuSection({
  title,
  links,
  viewAllHref,
  viewAllLabel = "View all",
  onClose,
}: {
  title: string;
  links: MenuLink[];
  viewAllHref?: string;
  viewAllLabel?: string;
  onClose: () => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {viewAllHref && (
          <Link
            to={viewAllHref}
            onClick={onClose}
            className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
          >
            {viewAllLabel}
            <FiArrowRight className="size-3" />
          </Link>
        )}
      </div>
      <ul className="space-y-1">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              to={link.href}
              onClick={onClose}
              className="flex items-start gap-3 p-2 -mx-2 rounded-lg hover:bg-muted transition-colors group"
            >
              <span className="text-muted-foreground group-hover:text-primary transition-colors mt-0.5">
                {link.icon}
              </span>
              <div>
                <span className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                  {link.label}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {link.description}
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface MegaMenuProps {
  type: "services" | "resources";
  onClose: () => void;
}

export function MegaMenu({ type, onClose }: MegaMenuProps) {
  if (type === "services") {
    return (
      <div className="absolute top-full left-0 right-0 bg-white border-b border-border/50 shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-12 gap-8">
            {/* Home Loans */}
            <div className="col-span-4">
              <MenuSection
                title="Home Loans"
                links={homeLoansLinks}
                viewAllHref="/home-loans"
                onClose={onClose}
              />
            </div>

            {/* Business Loans */}
            <div className="col-span-3">
              <MenuSection
                title="Business Loans"
                links={businessLinks}
                viewAllHref="/business-loans"
                onClose={onClose}
              />
            </div>

            {/* Featured */}
            <div className="col-span-5 pl-8 border-l border-border">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Featured
              </h3>
              <div className="grid gap-3">
                <Link
                  to="/guides/first-home-buyer-guide"
                  onClick={onClose}
                  className="block p-4 rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-md transition-all group"
                >
                  <span className="inline-block text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full mb-2">
                    Popular
                  </span>
                  <h4 className="font-medium text-sm group-hover:text-primary transition-colors">
                    First Home Buyer Guide
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Everything about grants, schemes, and buying your first home.
                  </p>
                </Link>
                <Link
                  to="/calculators/borrowing-power"
                  onClick={onClose}
                  className="block p-4 rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-md transition-all group"
                >
                  <span className="inline-block text-xs font-medium text-primary bg-accent/30 px-2 py-0.5 rounded-full mb-2">
                    Calculator
                  </span>
                  <h4 className="font-medium text-sm group-hover:text-primary transition-colors">
                    Borrowing Power Calculator
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Find out how much you could borrow based on your income.
                  </p>
                </Link>
              </div>
              <div className="mt-6 pt-4 border-t border-border">
                <Link
                  to="/contact"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                  Book a free consultation
                  <FiArrowRight className="size-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Resources menu
  return (
    <div className="absolute top-full left-0 right-0 bg-white border-b border-border/50 shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Calculators */}
          <div className="col-span-4">
            <MenuSection
              title="Calculators"
              links={calculatorLinks}
              viewAllHref="/calculators"
              viewAllLabel="All calculators"
              onClose={onClose}
            />
          </div>

          {/* Guides */}
          <div className="col-span-4">
            <MenuSection
              title="Guides & Resources"
              links={guideLinks}
              viewAllHref="/guides"
              viewAllLabel="All guides"
              onClose={onClose}
            />
          </div>

          {/* Quick Links */}
          <div className="col-span-4 pl-8 border-l border-border">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Quick Links
            </h3>
            <div className="space-y-3">
              <Link
                to="/market-news"
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-xl bg-cream hover:bg-cream/80 transition-colors"
              >
                <div className="p-2 bg-white rounded-lg">
                  <FiTrendingUp className="size-5 text-primary" />
                </div>
                <div>
                  <span className="font-medium text-sm">RBA News & Updates</span>
                  <p className="text-xs text-muted-foreground">Market news</p>
                </div>
              </Link>
              <Link
                to="/lenders"
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-xl bg-cream hover:bg-cream/80 transition-colors"
              >
                <div className="p-2 bg-white rounded-lg">
                  <FiUsers className="size-5 text-primary" />
                </div>
                <div>
                  <span className="font-medium text-sm">Our Lender Panel</span>
                  <p className="text-xs text-muted-foreground">70+ lenders</p>
                </div>
              </Link>
              <Link
                to="/blog"
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-xl bg-cream hover:bg-cream/80 transition-colors"
              >
                <div className="p-2 bg-white rounded-lg">
                  <FiFileText className="size-5 text-primary" />
                </div>
                <div>
                  <span className="font-medium text-sm">Blog</span>
                  <p className="text-xs text-muted-foreground">Tips & insights</p>
                </div>
              </Link>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <Link
              to="/disclaimer"
              onClick={onClose}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Disclaimer
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
